import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { User, Settings, HelpCircle, LogOut, Sparkles, Shield, Moon, Sun, Monitor } from 'lucide-react';
import { Avatar } from '@/components/ui/Avatar';
import { usePermissions } from '@/hooks/usePermissions';
import { useUIStore } from '@/stores/uiStore';
import type { Profile } from '@/types/database';

interface UserMenuDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
  user: { email?: string | null } | null;
  profile: Profile | null;
  onSignOut: () => void;
  /**
   * Where the menu opens relative to its trigger.
   *   bottom-end — below, right edges aligned (a top-bar avatar)
   *   top-start  — above, left edges aligned (the sidebar's account row)
   */
  placement?: 'bottom-end' | 'top-start';
}

const GAP = 8;
const MARGIN = 8;
const MENU_WIDTH = 288;

const THEMES = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
] as const;

export function UserMenuDropdown({
  isOpen,
  onClose,
  triggerRef,
  user,
  profile,
  onSignOut,
  placement = 'bottom-end',
}: UserMenuDropdownProps) {
  const navigate = useNavigate();
  const theme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }
    const triggerEl = triggerRef.current;
    const menuEl = menuRef.current;
    if (!triggerEl || !menuEl) return;

    // Viewport coordinates throughout: the menu is position: fixed, so adding
    // the page's scroll offset (as this once did) pushed it off-screen by
    // exactly as far as the page had been scrolled.
    const t = triggerEl.getBoundingClientRect();
    const m = menuEl.getBoundingClientRect();

    const wantedTop = placement === 'top-start' ? t.top - GAP - m.height : t.bottom + GAP;
    const wantedLeft = placement === 'top-start' ? t.left : t.right - m.width;

    setPosition({
      top: Math.max(MARGIN, Math.min(wantedTop, window.innerHeight - m.height - MARGIN)),
      left: Math.max(MARGIN, Math.min(wantedLeft, window.innerWidth - m.width - MARGIN)),
    });
  }, [isOpen, placement, triggerRef]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node) &&
        !(event.target as Element).closest('[data-user-menu-dropdown]')
      ) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        triggerRef.current?.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      window.addEventListener('resize', onClose);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', onClose);
    };
  }, [isOpen, onClose, triggerRef]);

  const handleAction = (action: string) => {
    switch (action) {
      case 'profile':
        navigate('/settings/profile');
        onClose();
        break;
      case 'settings':
        navigate('/settings');
        onClose();
        break;
      case 'whats-new':
        navigate('/whats-new');
        onClose();
        break;
      case 'help':
        navigate('/help');
        onClose();
        break;
      case 'admin':
        navigate('/admin');
        onClose();
        break;
      case 'logout':
        onSignOut();
        onClose();
        break;
    }
  };

  // Server-decided (check_flow_access().is_admin, migration 181) so the menu
  // entry and the Admin Center route guard can never disagree.
  const { isAdminUser: isAdmin } = usePermissions();

  const menuItems = [
    { label: 'Profile', value: 'profile', icon: User },
    { label: 'Settings', value: 'settings', icon: Settings },
    { label: "What's New", value: 'whats-new', icon: Sparkles },
    { label: 'Help Center', value: 'help', icon: HelpCircle },
    ...(isAdmin ? [{ label: 'Admin', value: 'admin', icon: Shield }] : []),
  ];

  const displayName = profile?.full_name || user?.email || 'User';
  const displayEmail = profile?.full_name ? user?.email : null;

  if (!isOpen) return null;

  const itemClass =
    'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm font-medium transition-colors focus-visible:outline-none';

  return createPortal(
    <div
      ref={menuRef}
      data-user-menu-dropdown
      role="menu"
      aria-label="Account"
      className={cn(
        'fixed z-[60] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-elevated',
        'dark:border-gray-700 dark:bg-gray-800',
        position && (placement === 'top-start' ? 'animate-fade-in-up' : 'animate-fade-in')
      )}
      style={{
        width: MENU_WIDTH,
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        visibility: position ? 'visible' : 'hidden',
      }}
    >
      {/* Who is signed in */}
      <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3.5 dark:border-gray-700">
        <Avatar src={profile?.avatar_url} name={displayName} size="md" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
            {displayName}
          </div>
          {displayEmail && (
            <div className="truncate text-xs text-gray-500 dark:text-gray-400">{displayEmail}</div>
          )}
          {profile?.role && (
            <div className="mt-1 inline-flex rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-medium capitalize text-primary-700 ring-1 ring-inset ring-primary-200/60 dark:bg-primary-900/30 dark:text-primary-300 dark:ring-primary-700/40">
              {profile.role.replace(/_/g, ' ')}
            </div>
          )}
        </div>
      </div>

      {/* Destinations */}
      <div className="p-1.5">
        {menuItems.map((item) => (
          <button
            key={item.value}
            type="button"
            role="menuitem"
            onClick={() => handleAction(item.value)}
            className={cn(
              itemClass,
              'text-gray-700 hover:bg-gray-100 focus-visible:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 dark:focus-visible:bg-gray-700'
            )}
          >
            <item.icon className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />
            {item.label}
          </button>
        ))}
      </div>

      {/* Theme — chosen in place; the menu stays open so the change is seen. */}
      <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-700">
        <div className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
          Theme
        </div>
        <div
          role="radiogroup"
          aria-label="Theme"
          className="grid grid-cols-3 gap-0.5 rounded-lg border border-gray-200/80 bg-gray-100/80 p-1 dark:border-gray-700 dark:bg-gray-900"
        >
          {THEMES.map((option) => {
            const selected = theme === option.value;
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setTheme(option.value)}
                className={cn(
                  'flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-all',
                  'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary-500/40',
                  selected
                    ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-900/5 dark:bg-gray-700 dark:text-gray-100 dark:ring-white/10'
                    : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                )}
              >
                <option.icon className="h-3.5 w-3.5" aria-hidden="true" />
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Leave */}
      <div className="border-t border-gray-100 p-1.5 dark:border-gray-700">
        <button
          type="button"
          role="menuitem"
          onClick={() => handleAction('logout')}
          className={cn(
            itemClass,
            'text-danger-600 hover:bg-danger-50 focus-visible:bg-danger-50 dark:text-danger-400 dark:hover:bg-danger-900/30 dark:focus-visible:bg-danger-900/30'
          )}
        >
          <LogOut className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          Sign out
        </button>
      </div>
    </div>,
    document.body
  );
}
